import { useMemo } from "react";

import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { TamaguiProvider } from "@tamagui/core";
import { Auth0Provider } from "react-native-auth0";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Provider as ReduxProvider } from "react-redux";

import LaunchDarklyIdentifyProvider from "@/context-providers/LaunchDarklyIdentifyProvider";
import OnboardingProvider from "@/context-providers/OnboardingProvider";
import SafeNativeModalProvider from "@/context-providers/SafeNativeModalProvider";
import { SchedulePermissionsContextProvider } from "@/context-providers/SchedulePermissionsContext";
import { UserActivityProvider } from "@/context-providers/UserActivityProvider";
import useLaunchDarkly from "@/crossPlatform/LaunchDarkly";
import { AnalyticsProvider } from "@/services/Analytics";
import { Auth0ClientOptions } from "@/services/Auth";
import { SendbirdProviders } from "@/services/Messages";
import store from "@/store";
import ErrorBoundaryProvider from "@/ui/components/errorBoundary/ErrorBoundaryProvider";
import DeepLinkingProvider from "@/ui/components/navigation/DeepLinkingProvider";
import SplashScreenProvider from "@/ui/components/splashScreen/SplashScreenProvider";
import ToastProvider from "@/ui/composed/toast/ToastProvider";
import tamaguiConfig from "@/ui/theme/tamagui.config";
import { isWeb } from "@/utils/platform.util";
import { buildProvidersTree } from "@/utils/providerTree.util";

// on web:
// 1. user visits this app -> gets routed to app/(login) -> redirected to auth0.com/login -> redirected back to app's /myequip callback
// 2a. withLDProviders(RootLayout) renders the RootLayout, with LD initially using the fallback values for feature flags
// 2b. the Auth0Provider immediately begins running its post-login-callback init, reading and writing session storage (NOT idempotently)
// 3. LD receives its actual feature flag values -> updates its useContext -> triggers a full re-render of RootLayout
// 4. if unmemoized, the ProviderTree then unmounts and re-mounts with a new instance of each Provider
// 5. the new Auth0Provider instance also detects "post-login-callback" and begins its own init
// 6. the two Auth0Provider instances race to do the destructive (not-idempotent) init
// 7. whichever instance goes last fails, and causes an Auth0 "unrecoverable init error" to be (correctly) produced
// 8. user then gets redirected to auth0's /v2/logout to wipe their "unrecoverable" auth0 session
// 9. auth0's /v2/logout routes back to app, which detects user has no Auth0 session, and routes to auth0.com/login; rinse and repeat
// so: the ProviderTree must be memoized to prevent this infinite login loop
// on mobile: 1) LDProvider is a child (not parent) of RootLayout, 2) Auth0 login does not do this /callback + redirect flow

// eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
const useGlobalAppProviders = () => {
  const { LDProviders } = useLaunchDarkly();

  const gestureHandlerStyle = { flex: 1 };
  const ProviderTree = buildProvidersTree([
    [GestureHandlerRootView, { style: gestureHandlerStyle }],
    [KeyboardProvider, { shouldPreload: true }],
    [TamaguiProvider, { config: tamaguiConfig, defaultTheme: "light" }],
    ...LDProviders,
    [Auth0Provider, Auth0ClientOptions],
    [ReduxProvider, { store }],
    [AnalyticsProvider, {}],
    // ErrorBoundaryProvider depends on Redux state (auth0RenewFailure), so it must be inside ReduxProvider.
    [ErrorBoundaryProvider, {}],
    ...SendbirdProviders,
    [SafeAreaProvider, {}],
    [ToastProvider, {}],
    // BottomSheetModalProvider must be AFTER Redux/Toast so modals have context,
    // but BEFORE navigation/routing providers so it can render above modal screens
    [BottomSheetModalProvider, {}],
    [SplashScreenProvider, {}],
    [SafeNativeModalProvider, {}],
    [LaunchDarklyIdentifyProvider, {}],
    [UserActivityProvider, {}],
    [DeepLinkingProvider, {}],
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- web ProviderTree must be memoized
  const memoizedProviderTree = useMemo(() => ProviderTree, []);

  // on mobile, no change, to minimize potential impact of web-only changes
  return isWeb() ? memoizedProviderTree : ProviderTree;
};

// eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
const useAuthenticatedAppProviders = () => {
  const ProviderTree = buildProvidersTree([
    [OnboardingProvider, {}],
    [SchedulePermissionsContextProvider, {}],
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- web ProviderTree must be memoized
  const memoizedProviderTree = useMemo(() => ProviderTree, []);

  // on mobile, no change, to minimize potential impact of web-only changes
  return isWeb() ? memoizedProviderTree : ProviderTree;
};

export { useAuthenticatedAppProviders, useGlobalAppProviders };
